import { ArrowRightIcon } from '@/assets';
import Link from 'next/link';
import { CheckBadge } from '@/assets/CheckBadge';

interface TiersProps {
  name: string;
  features: FeatureProps[];
  featured?: boolean;
  bgColor?: string;
  head?: boolean;
}
interface FeatureProps {
  title: string;
  subtitle?: string;
}

const tiers: TiersProps[] = [
  {
    name: '\n\n',
    features: [
      { title: 'Cost Structure' },
      { title: 'Time-to-Hire' },
      { title: 'Flexibility' },
      { title: 'Quality of Talent' },
      { title: 'Scalability' },
      { title: 'Risk' },
      { title: 'Management\n Overhead' },
    ],
    bgColor: 'regular', //Regular
    head: true,
  },
  {
    name: 'Talent as a Service (TaaS)',
    featured: true,
    features: [
      { title: 'Project-based fee' },
      { title: 'Days', subtitle: 'Pre-vetted talent ready to deploy' },
      { title: 'On-demand scaling' },
      { title: 'Pre-vetted experts' },
      { title: 'High' },
      { title: 'Low', subtitle: 'No long-term contracts;\nreplace easily' },
      { title: 'Minimal', subtitle: 'TaaS provider manages talent' },
    ],
    bgColor: 'highlight', //Highlight
  },
  {
    name: 'Recruiting Agencies',
    features: [
      { title: "15-30% of hire's salary" },
      { title: 'Weeks to months' },
      { title: 'Limited flexibility' },
      { title: 'Variable quality' },
      { title: 'Moderate' },
      { title: 'Moderate risk', subtitle: 'Bad hires = lost fees + time' },
      { title: 'Moderate' },
    ],
    bgColor: 'regular',
  },
  {
    name: 'In-House Hiring\n\n',
    features: [
      { title: 'Salary + overhead' },
      { title: '3-6+ months' },
      { title: 'Fixed long-term\ncommitment' },
      { title: 'Time-consuming to vet' },
      { title: 'Low' },
      { title: 'High risk', subtitle: 'Costly layoffs if needs change' },
      { title: 'High' },
    ],
    bgColor: 'regular',
  },
];

const featuredHead = tiers.find((tier) => tier.head)?.features ?? [];

export const TaasTable = () => {
  return (
    <section
      className="container mx-auto px-6 md:px-16 py-[30px] scroll-mt-20"
      id="expertise"
    >
      <div className="flex flex-col items-center gap-y-4">
        <h3 className="text-4xl md:text-5xl font-bold text-center">
          Why Choose Talent as a Service (TaaS)
        </h3>
      </div>

      <div className="grid">
        <div className="mt-8 pb-0 md:pb-12 sm:mt-12 sm:pb-16 lg:mt-16 lg:pb-24">
          <div className="relative">
            <div className="absolute inset-0 h-3/4" />
            <div className="relative z-10 sm:px-6 lg:px-0 ">
              <div className="lg:grid lg:grid-cols-4 lg:gap-2 lg:space-y-0">
                {tiers.map((tier, key) => (
                  <div
                    key={key}
                    className={`flex flex-col rounded-2xl overflow-hidden mb-3
                              ${tier.head ? 'hidden sm:flex' : ''}
                              ${tier.bgColor === 'regular' ? '' : 'pb-[1rem]'}
                              px-2
                              `}
                    style={{
                      backgroundImage: tier.name.trim()
                        ? tier.bgColor === 'regular'
                          ? 'linear-gradient(to bottom, rgba(0, 0, 0, 0.185), rgba(0, 0, 0, 0))'
                          : 'linear-gradient(to right, rgba(255,255,255,0.15), rgba(3, 191, 158, 0.15), rgba(255,255,255,0.15))'
                        : '',
                    }}
                  >
                    {tier.name && (
                      <div
                        className={`
                                  ${tier.name.trim() ? `p-6 flex flex-col text-start justify-center rounded-t-3xl` : 'py-6'}
                                  ${
                                    tier.name.trim()
                                      ? tier.bgColor === 'regular'
                                        ? 'border-b border-b-[#026846]'
                                        : 'border-b border-b-[#002726]'
                                      : 'no-border'
                                  }
                                  `}
                      >
                        <h2 className="text-xl md:text-2xl font-bold text-start whitespace-pre-line">
                          {tier.name}
                        </h2>
                      </div>
                    )}

                    {/* This outer div is specifically so that the first column has its own styling */}
                    <div
                      id={`left-most-column-${key}`}
                      className={`flex flex-col rounded-t-3xl rounded-b-3xl ${tier.name.trim() ? '' : 'px-2'}`}
                      style={{
                        backgroundImage: tier.name.trim()
                          ? ''
                          : 'linear-gradient(to bottom, rgba(0, 0, 0, 0.185), rgba(0, 0, 0, 0))',
                      }}
                    >
                      {tier.features.map((feature, index) => (
                        <div
                          key={index}
                          className={`flex flex-col items-start justify-center h-[100px] 
                                  ${feature.subtitle ? 'flex-col' : ''}
                                  ${tier.head ? 'pl-4' : 'justify-center'}
                                  ${index === 0 && tier.name.trim() === '' ? 'rounded-t-3xl' : ''}
                                  ${
                                    index === tier.features.length - 1
                                      ? 'no-border'
                                      : tier.name.trim()
                                        ? tier.bgColor === 'regular'
                                          ? 'border-b border-b-[#026846]'
                                          : 'border-b border-b-[#002726]'
                                        : 'border-b border-b-[#026846]'
                                  }
                                  `}
                        >
                          <div className="p-1 flex items-center">
                            {tier.featured && <CheckBadge />}
                            <span className="ml-1 mt-1 text-xl font-bold text-start whitespace-break-spaces">
                              <span className=" block sm:hidden text-center text-sm font-light">
                                {featuredHead[index]?.title.toUpperCase()}:
                              </span>
                              {feature.title}
                            </span>
                          </div>

                          <span
                            className={`text-[14px] text-start ${tier.featured ? 'pl-[2rem] whitespace-break-spaces' : 'pl-[0.5rem]'}`}
                          >
                            {feature.subtitle}
                          </span>
                        </div>
                      ))}
                      {tier.featured && (
                        <Link
                          href="/get-started/augmentation"
                          className="secondary-button self-center justify-center w-[90%] mt-[1rem]"
                        >
                          <ArrowRightIcon className="arrow-icon" />
                          Start Scaling Today
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
